// src/context/UnifiedAuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { staticApi } from '../pages/condoManagement/utils/staticApi';
import { useApiService } from '../service/UnifiedApiService'; // Adjust path as needed
import { useAuthStore, User as CondoUserType } from '../pages/condoManagement/store/authStore';
import { getAccessToken as getCondoAccessToken } from '../pages/condoManagement/services/httpClient';

// ====================== UNIFIED USER ROLES ======================
export type AppType = 'medical' | 'condo' | 'tenant' | 'general';
export type MedicalUserRole = 'patient' | 'doctor' | 'admin';
export type CondoUserRole = 'owner' | 'admin' | 'super_admin_condo' | 'board-member';
export type UserRole = MedicalUserRole | CondoUserRole | string;

// ====================== UNIFIED USER INTERFACE ======================
export interface BaseUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  name: string;
  role: UserRole;
  appType: AppType;
  avatar?: string;
  lastLogin?: Date;
  metadata?: Record<string, any>;
  permissions?: string[]; // Added for condo compatibility
}

export interface MedicalUser extends BaseUser {
  appType: 'medical';
  role: MedicalUserRole;
  firstName?: string;
  lastName?: string;
  patientId?: string;
  doctorLicense?: string;
}

export interface CondoUser extends BaseUser {
  appType: 'condo';
  role: CondoUserRole;
  apartmentNumber?: string;
  buildingId?: string;
  unitNumber?: string;
  condoId?: string;
  ownerId?: string;
  isActive: boolean;
  permissions: string[];
  createdAt?: string;
  updatedAt?: string;
}

export type UnifiedUser = MedicalUser | CondoUser | BaseUser;

// ====================== AUTH CONTEXT INTERFACE ======================
export interface AuthContextType {
  // Core auth state
  user: UnifiedUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  currentApp: AppType | null;
  authToken: string | null;
  
  // Authentication methods
  login: (email: string, password: string, appType: AppType) => Promise<boolean>;
  logout: (appType?: AppType) => void;
  switchApp: (appType: AppType) => Promise<boolean>;
  resetError: () => void;
  
  // Token management
  getAuthToken: (appType?: AppType) => string | null;
  setAuthToken: (token: string, appType: AppType) => void;
  clearAuthToken: (appType?: AppType) => void;
  
  // User management
  register: (appType: AppType, userData: RegisterData) => Promise<boolean>;
  updateProfile: (userData: Partial<UnifiedUser>) => Promise<boolean>;
  clearError: () => void;
  
  // Role checking
  hasRole: (role: UserRole | UserRole[]) => boolean;
  hasPermission: (permission: string) => boolean;
}

// ====================== REGISTER DATA INTERFACE ======================
export interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone: string;
  role: UserRole;
  appType: AppType;
  metadata?: Record<string, any>;
}

// ====================== TOKEN MANAGEMENT ======================
const TOKEN_PREFIX = 'unified_auth_';

// Sprint 110 (110.1) — access tokens are held IN MEMORY only (a module map),
// never localStorage. Persisting a copy of the JWT under
// `unified_auth_<app>_token` re-introduced the XSS bearer-theft surface this
// sprint closes. The non-sensitive user blob + current-app preference stay in
// localStorage (used to restore UI state on reload); the token is re-minted on
// reload by the per-vertical auth flow (the condo app via its httpClient
// bootstrap against the HttpOnly refresh cookie).
const inMemoryUnifiedTokens: Record<string, string> = {};

const getAppTokenKey = (appType: AppType): string => {
  // Retained as the in-memory map key (no longer a localStorage key).
  return `${TOKEN_PREFIX}${appType}_token`;
};

const getAppUserKey = (appType: AppType): string => {
  return `${TOKEN_PREFIX}${appType}_user`;
};

const getCurrentAppKey = (): string => {
  return `${TOKEN_PREFIX}current_app`;
};

// ====================== CREATE CONTEXT ======================
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ====================== CUSTOM HOOK ======================
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a UnifiedAuthProvider');
  }
  return context;
};

// ====================== AUTH PROVIDER PROPS ======================
interface UnifiedAuthProviderProps {
  children: ReactNode;
  defaultApp?: AppType;
}

// Helper function to convert CondoUserType to UnifiedUser
const convertCondoUserToUnified = (condoUser: CondoUserType): CondoUser => {
  return {
    id: condoUser.id,
    email: condoUser.email,
    name: condoUser.name || '',
    role: condoUser.role,
    appType: 'condo' as const,
    condoId: condoUser.condoId,
    ownerId: condoUser.ownerId,
    permissions: condoUser.permissions || [],
    isActive: condoUser.isActive,
    avatar: condoUser.avatar,
    phone: condoUser.phone,
    createdAt: condoUser.createdAt,
    updatedAt: condoUser.updatedAt,
    metadata: {
      originalRole: condoUser.role,
      condoData: condoUser
    }
  };
};

export const UnifiedAuthProvider: React.FC<UnifiedAuthProviderProps> = ({ 
  children, 
  defaultApp = 'general' 
}) => {
  const [user, setUser] = useState<UnifiedUser | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentApp, setCurrentApp] = useState<AppType | null>(null);
  const [authToken, setAuthTokenState] = useState<string | null>(null);
  
  // Use the unified API service
  const apiService = useApiService();
  
  // Access the existing condo auth store
  const condoAuthStore = useAuthStore();

  // ====================== TOKEN MANAGEMENT HELPERS ======================
  const getStoredToken = useCallback((appType: AppType): string | null => {
    // Sprint 110 (110.1) — in-memory only.
    return inMemoryUnifiedTokens[getAppTokenKey(appType)] ?? null;
  }, []);

  const setStoredToken = useCallback((token: string, appType: AppType): void => {
    inMemoryUnifiedTokens[getAppTokenKey(appType)] = token;
    setAuthTokenState(token);
  }, []);

  const clearStoredToken = useCallback((appType?: AppType): void => {
    if (appType) {
      delete inMemoryUnifiedTokens[getAppTokenKey(appType)];
      // Defensive: purge any token a prior build persisted.
      try { localStorage.removeItem(getAppTokenKey(appType)); } catch { /* best effort */ }
    } else {
      // Clear all app tokens
      (['medical', 'condo', 'tenant', 'general'] as AppType[]).forEach(app => {
        delete inMemoryUnifiedTokens[getAppTokenKey(app)];
        try { localStorage.removeItem(getAppTokenKey(app)); } catch { /* best effort */ }
      });
    }
    setAuthTokenState(null);
  }, []);

  // ====================== INITIALIZATION ======================
  useEffect(() => {
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        // Check for current app preference
        const savedApp = localStorage.getItem(getCurrentAppKey()) as AppType;
        const appToCheck = savedApp || defaultApp;
        setCurrentApp(appToCheck);
        
        // Sprint 110 (110.1) — the access token is in memory only and is empty
        // on a fresh page load, so we can't gate restoration on a persisted
        // token anymore. For the CONDO app, drive restoration off the condo
        // store's checkAuth(), which silently re-mints the access token from the
        // HttpOnly refresh cookie (bootstrap). For other verticals, fall back to
        // any in-memory token (set during this page-life by login).
        const savedUser = localStorage.getItem(getAppUserKey(appToCheck));

        if (appToCheck === 'condo' && condoAuthStore) {
          // Re-hydrate the condo session from the refresh cookie. Only restore
          // the unified user view if the bootstrap actually produced a session.
          if (!condoAuthStore.isAuthenticated) {
            await condoAuthStore.checkAuth();
          }
          const fresh = useAuthStore.getState();
          const reMintedToken = getCondoAccessToken();
          if (fresh.isAuthenticated && reMintedToken) {
            inMemoryUnifiedTokens[getAppTokenKey('condo')] = reMintedToken;
            setAuthTokenState(reMintedToken);
            if (savedUser) {
              try { setUser(JSON.parse(savedUser)); }
              catch (err) { console.error('Error parsing saved user:', err); }
            }
          }
        } else {
          const token = getStoredToken(appToCheck);
          if (token) {
            setAuthTokenState(token);
            if (savedUser) {
              try {
                setUser(JSON.parse(savedUser));
              } catch (err) {
                console.error('Error parsing saved user:', err);
                logout(appToCheck);
              }
            }
          }
        }
      } catch (err) {
        console.error('Auth initialization error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-time auth bootstrap keyed on token/app; condoAuthStore + logout are intentionally not deps to avoid re-initializing the session
  }, [getStoredToken, defaultApp]);

  // ====================== LOGIN FUNCTION ======================
  const login = async (
    email: string, 
    password: string, 
    appType: AppType
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    // Sprint 32 — finding R1. antd's message.* APIs render to a global
    // stack that survives across submissions: a failed login's
    // message.error('Invalid email or password.') used to sit on screen
    // even after the next attempt succeeded and called message.success(),
    // so the user saw both toasts stacked. Destroy the existing stack
    // before issuing the new login so only the latest attempt's outcome
    // is visible.
    staticApi.message.destroy();
    setCurrentApp(appType);
    
    try {
      // Handle different app types
      switch (appType) {
        case 'condo':
           // Do NOT hard-redirect via window.location.href here. A full page
           // reload tears down the Zustand auth store and forces rehydration
           // from localStorage; the rehydration is async in Zustand v5, so
           // ProtectedRoute renders with user=null on first paint and fires
           // checkAuth() which used to hit the wrong endpoint and wipe the
           // token — leaving the user stuck on "Verifying session...".
           // The caller (Login.tsx) now navigates via React Router on true.
           return await handleCondoLogin(email, password);
          
        case 'medical':
          return await handleMedicalLogin(email, password);
          
        default:
          // Generic login for other app types
          const response = await apiService.loginApi(appType, email, password);
          
          if (response.token && response.user) {
            const userData = {
              ...response.user,
              appType,
            };
            
            saveAuthData(userData, response.token, appType);
            setUser(userData);
            staticApi.message.success(`Login to ${appType} app successful!`);
            return true;
          }
          return false;
      }
    } catch (err: any) {
      const raw = err instanceof Error ? err.message : 'Login failed';
      const errorMessage = (!raw || raw === 'No message available')
        ? 'Invalid email or password. Please try again.'
        : raw;
      // Sprint 35 — finding G10. Was emitting BOTH setError() (which the
      // login form's inline Alert renders) AND message.error() at the
      // top of the page — same text, two surfaces, stacked toast +
      // banner that made the page look broken. Keep the inline Alert
      // (closer to the form, dismissible) and drop the top toast.
      // The success-path toast at line 339 stays — it's the
      // navigation-irrelevant ack that the redirect doesn't carry.
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== CONDOS SPECIFIC LOGIN ======================
  const handleCondoLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      // Use the existing auth store for condo login
      await condoAuthStore.login(email, password);

      // Read CURRENT state via getState() — condoAuthStore is a Zustand snapshot
      // captured at render time and won't reflect the post-login update.
      const freshState = useAuthStore.getState();
      if (freshState.user && freshState.isAuthenticated) {
        // Convert condo user to unified user format
        const unifiedUser = convertCondoUserToUnified(freshState.user);

        // Sprint 28 — finding F8. Was fabricating
        // `condo-token-${Date.now()}-${user.id}`, which then sat in
        // localStorage under `unified_auth_condo_token` looking like a real
        // session token but matched nothing the backend issues. The actual
        // access token is the JWT the condo authStore -> httpClient.setTokens
        // already stored under `authToken`; mirror that into the unified
        // store so consumers of `useAuth()` see a real token, not a fake.
        // If the access token is somehow missing (refresh-only flow,
        // off-nominal), fall back to an empty string rather than fabricate.
        const token = getCondoAccessToken() ?? '';

        saveAuthData(unifiedUser, token, 'condo');
        setUser(unifiedUser);
        
        // Set current condo if available (use fresh state)
        if (freshState.currentCondo) {
          unifiedUser.metadata = {
            ...unifiedUser.metadata,
            currentCondo: freshState.currentCondo
          };
        }
        
        staticApi.message.success('Condo login successful!');
        return true;
      }
      
      throw new Error('Condo login failed');
    } catch (err: any) {
      throw new Error(err.message || 'Condo login failed');
    }
  };

  // ====================== MEDICAL SPECIFIC LOGIN ======================
  const handleMedicalLogin = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await apiService.loginApi('medical', email, password);
      
      if (response.token && response.user) {
        const medicalUser: MedicalUser = {
          ...response.user,
          appType: 'medical',
        };
        
        saveAuthData(medicalUser, response.token, 'medical');
        setUser(medicalUser);
        staticApi.message.success('Medical login successful!');
        return true;
      }
      
      throw new Error('Medical login failed');
    } catch (err: any) {
      throw new Error(err.message || 'Medical login failed');
    }
  };

  // ====================== LOGOUT FUNCTION ======================
  const logout = (appType?: AppType): void => {
    const targetApp = appType || currentApp;
    
    if (targetApp) {
      // Clear tokens and user data for the specific app
      clearStoredToken(targetApp);
      localStorage.removeItem(getAppUserKey(targetApp));
      
      // If logging out from condo, also clear the condo auth store
      if (targetApp === 'condo' && condoAuthStore) {
        condoAuthStore.logout();
      }
    } else {
      // Clear all auth data
      clearStoredToken();
      Object.values(['medical', 'condo', 'tenant', 'general'] as AppType[]).forEach(app => {
        localStorage.removeItem(getAppUserKey(app));
      });
      
      // Clear condo auth store
      if (condoAuthStore) {
        condoAuthStore.logout();
      }
    }
    
    // Clear any remaining auth keys that other mechanisms may have written.
    // Sprint 2 added `refreshToken` (rotation chain) — must be cleared here
    // too. Otherwise the httpClient wrapper still has a valid refresh and
    // can silently re-mint an access token, defeating the logout entirely.
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');

    setUser(null);
    setCurrentApp(null);
    staticApi.message.info('You have been logged out');
    window.location.href = '/login-condo-app/';
  };

  // ====================== APP SWITCHING ======================
  const switchApp = async (appType: AppType): Promise<boolean> => {
    setIsLoading(true);
    
    try {
      // Check if user has credentials for the new app
      const token = getStoredToken(appType);
      const savedUser = localStorage.getItem(getAppUserKey(appType));
      
      if (token && savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        setCurrentApp(appType);
        setAuthTokenState(token);
        localStorage.setItem(getCurrentAppKey(), appType);
        return true;
      }
      
      // If no saved credentials, user needs to login
      setError(`Please login to the ${appType} app`);
      return false;
    } catch (err) {
      setError('Failed to switch app');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== TOKEN MANAGEMENT FUNCTIONS ======================
  const getAuthToken = (appType?: AppType): string | null => {
    const targetApp = appType || currentApp;
    if (!targetApp) return null;
    
    return getStoredToken(targetApp);
  };

  const setAuthToken = (token: string, appType: AppType): void => {
    setStoredToken(token, appType);
  };

  // ====================== HELPER FUNCTIONS ======================
  const saveAuthData = (userData: UnifiedUser, token: string, appType: AppType): void => {
    setStoredToken(token, appType);
    localStorage.setItem(getAppUserKey(appType), JSON.stringify(userData));
    localStorage.setItem(getCurrentAppKey(), appType);
  };

  const resetError = (): void => {
    setError(null);
  };

  const clearError = (): void => {
    setError(null);
  };

  // ====================== REGISTER FUNCTION ======================
  const register = async (appType: AppType, userData: RegisterData): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // Use the unified API service for registration
      const response = await (apiService as any).register(appType, userData);
      
      if (response.token && response.user) {
        const newUser: UnifiedUser = {
          ...response.user,
          appType: userData.appType,
        };
        
        saveAuthData(newUser, response.token, userData.appType);
        setUser(newUser);
        setCurrentApp(userData.appType);
        
        staticApi.message.success(`Registration for ${userData.appType} app successful!`);
        return true;
      }
      
      return false;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== UPDATE PROFILE ======================
  const updateProfile = async (userData: Partial<UnifiedUser>): Promise<boolean> => {
    if (!user) {
      setError('No user logged in');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Handle app-specific profile updates
      if (user.appType === 'condo' && condoAuthStore.user) {
        // Update condo user profile
        await condoAuthStore.updateUserProfile({
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          avatar: userData.avatar,
        });
        
        // Sync with unified user
        const updatedUnifiedUser = convertCondoUserToUnified(condoAuthStore.user);
        setUser(updatedUnifiedUser);
        localStorage.setItem(getAppUserKey('condo'), JSON.stringify(updatedUnifiedUser));
      } else {
        // Use API for other app types
        const updatedUser = await (apiService as any).updateUserProfile(user.appType, userData);
        const unifiedUpdatedUser = { ...updatedUser, appType: user.appType };
        
        setUser(unifiedUpdatedUser);
        localStorage.setItem(getAppUserKey(user.appType), JSON.stringify(unifiedUpdatedUser));
      }
      
      staticApi.message.success('Profile updated successfully!');
      return true;
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : 'Update failed';
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // ====================== ROLE AND PERMISSION CHECKS ======================
  const hasRole = (role: UserRole | UserRole[]): boolean => {
    if (!user) return false;
    
    if (Array.isArray(role)) {
      return role.includes(user.role);
    }
    
    return user.role === role;
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    // Check condo permissions
    if (user.appType === 'condo' && 'permissions' in user) {
      return user.permissions?.includes(permission) || false;
    }
    
    // Generic permission logic for other apps
    const rolePermissions: Record<string, string[]> = {
      'admin': ['manage_users', 'view_analytics', 'system_settings'],
      'super_admin': ['all'],
      'doctor': ['view_patients', 'write_prescriptions', 'order_labs'],
      'patient': ['view_own_records', 'schedule_appointments'],
      'owner': ['view_billing', 'submit_requests'],
    };
    
    return rolePermissions[user.role]?.includes(permission) || false;
  };

  // ====================== CONTEXT VALUE ======================
  const contextValue: AuthContextType = {
    // State
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    currentApp,
    authToken,
    
    // Methods
    login,
    logout,
    switchApp,
    resetError,
    register,
    updateProfile,
    clearError,
    hasRole,
    hasPermission,
    getAuthToken,
    setAuthToken,
    clearAuthToken: clearStoredToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};